/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useMemo, useRef } from 'react';
import ApexCharts, { ApexOptions } from 'apexcharts';

interface Props {
  className: string;
  title: string;
  series: ApexAxisChartSeries | ApexNonAxisChartSeries | undefined;
  categories: Array<string>;
}

/**
 * Bars are horizontal, so the chart grows downwards with the number of time
 * buckets instead of squeezing them into a fixed width — 24 hours in a day view
 * needs far more room than 12 months in a year view.
 */
const ROW_HEIGHT = 42;
const CHART_CHROME = 110;
const MIN_CHART_HEIGHT = 320;

function chartHeightFor(categories: Array<string>) {
  return Math.max(
    MIN_CHART_HEIGHT,
    categories.length * ROW_HEIGHT + CHART_CHROME
  );
}

const PercentageDistButtonPresses: React.FC<React.PropsWithChildren<Props>> = ({
  className,
  title,
  series,
  categories,
}) => {
  const chartRef = useRef<HTMLDivElement | null>(null);

  // The API sends percentages as fixed-point strings ("12.34"); Apex needs numbers
  // for the value axis, the labels and the tooltip to agree.
  const numericSeries = useMemo(() => {
    if (!Array.isArray(series)) return series;
    return (series as any[]).map((group) =>
      group && Array.isArray(group.data)
        ? { ...group, data: group.data.map((value: any) => Number(value) || 0) }
        : group
    );
  }, [series]);

  const height = chartHeightFor(categories);

  useEffect(() => {
    if (!chartRef.current) {
      return;
    }

    const chart = new ApexCharts(
      chartRef.current,
      getChartOptions(numericSeries as any, categories, height)
    );
    if (chart) {
      chart.render();
    }

    return () => {
      if (chart) {
        chart.destroy();
      }
    };
  }, [chartRef, numericSeries, categories, height]);

  return (
    <div className={`card ${className}`}>
      {/* begin::Header */}
      <div className="card-header border-0 pt-5">
        <h3 className="card-title align-items-start flex-column">
          <span className="card-label fw-bolder fs-3 mb-1">{title}</span>
        </h3>

        <div className="card-toolbar" data-kt-buttons="true"></div>
      </div>

      <div className="card-body">
        <div
          ref={chartRef}
          id="kt_charts_widget_6_chart"
          style={{ height: `${height}px` }}
        ></div>
      </div>
    </div>
  );
};

export { PercentageDistButtonPresses };

function getChartOptions(
  series: ApexAxisChartSeries | ApexNonAxisChartSeries | undefined,
  categories: Array<string>,
  height: number
): ApexOptions {
  const labelColor = '#52514e';
  const borderColor = '#e5e5e2';

  return {
    series,
    chart: {
      type: 'bar',
      fontFamily: 'inherit',
      stacked: true,
      height,
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      bar: {
        horizontal: true,
        borderRadius: 5,
        barHeight: '70%',
        dataLabels: {
          // Keep each percentage centred in its own segment rather than at the
          // end of the stack.
          position: 'center',
        },
      },
    },
    legend: {
      position: 'bottom',
      horizontalAlign: 'center',
      markers: {
        radius: 2,
      },
      fontSize: '12px',
      labels: {
        colors: labelColor,
      },
    },
    dataLabels: {
      enabled: true,
      // A clipped number reads worse than none at all, and the tooltip carries
      // every value anyway — so only label segments wide enough to hold one.
      formatter: (val: number) =>
        Number(val) >= 8 ? `${Math.round(Number(val))}%` : '',
      style: {
        fontSize: '12px',
        fontWeight: 600,
        colors: ['#ffffff'],
      },
      dropShadow: {
        enabled: false,
      },
    },
    stroke: {
      width: 2,
      colors: ['#fff'],
    },
    // With horizontal bars Apex renders `xaxis.categories` down the y axis and
    // uses the x axis for the values, so the time buckets land on the left.
    xaxis: {
      categories,
      // Pin both ends: with only `max` set, Apex derives a negative minimum and
      // renders ticks below zero that a percentage split can never reach.
      min: 0,
      max: 100,
      tickAmount: 4,
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      labels: {
        formatter: (val: string) => `${Math.round(Number(val))}%`,
        style: {
          colors: labelColor,
          fontSize: '12px',
        },
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: labelColor,
          fontSize: '12px',
        },
      },
    },
    fill: {
      opacity: 1,
    },
    states: {
      normal: {
        filter: {
          type: 'none',
          value: 0,
        },
      },
      hover: {
        filter: {
          type: 'none',
          value: 0,
        },
      },
      active: {
        allowMultipleDataPointsSelection: false,
        filter: {
          type: 'none',
          value: 0,
        },
      },
    },
    tooltip: {
      style: {
        fontSize: '12px',
      },
      y: {
        formatter: function (val) {
          return `${Number(val).toFixed(2)}%`;
        },
      },
    },
    colors: ['#DB2524', '#EA8C28', '#CDCF00', '#7EB147', '#00803D'],
    grid: {
      borderColor: borderColor,
      strokeDashArray: 4,
      xaxis: {
        lines: {
          show: true,
        },
      },
      yaxis: {
        lines: {
          show: false,
        },
      },
      padding: {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
      },
    },
  };
}
